// src/pages/Home.jsx
import React, { useContext, useState, useEffect } from 'react';
import { useYouTube } from './useYoutube';
import { PlayerContext } from '../context/PlayerContext';
import { Play, TrendingUp, Clock, Headphones, Music2 } from 'lucide-react';
import './Home.css';

const MOODS = [
  { label: 'Chill', emoji: '😌', query: 'chill lofi music 2024' },
  { label: 'Party', emoji: '🎉', query: 'party hits 2024' },
  { label: 'Workout', emoji: '💪', query: 'gym workout music 2024' },
  { label: 'Focus', emoji: '🧠', query: 'focus study music 2024' },
  { label: 'Sad', emoji: '🌧️', query: 'sad songs hindi 2024' },
  { label: 'Romance', emoji: '❤️', query: 'romantic songs hindi 2024' },
  { label: 'Bhakti', emoji: '🙏', query: 'bhajan devotional songs' },
  { label: 'Indie', emoji: '🎸', query: 'indie hindi songs 2024' },
];

const Home = () => {
  const { playSong, selectedSong } = useContext(PlayerContext) || {};
  const { initialized, searchSongs, getTrendingMusic } = useYouTube();

  const [trending, setTrending] = useState([]);
  const [moodSongs, setMoodSongs] = useState([]);
  const [activeMood, setActiveMood] = useState(null);
  const [loadingTrend, setLoadingTrend] = useState(true);
  const [loadingMood, setLoadingMood] = useState(false);
  const [recentlyPlayed, setRecentlyPlayed] = useState([]);

  useEffect(() => {
    if (!initialized) return;
    (async () => {
      try {
        const t = await getTrendingMusic(12);
        setTrending(t || []);
      } finally {
        setLoadingTrend(false);
      }
    })();
    // Load recently played from localStorage
    try {
      const rp = JSON.parse(localStorage.getItem('aura_recent') || '[]');
      setRecentlyPlayed(rp.slice(0, 6));
    } catch (_) { }
  }, [initialized]);

  // Save to recently played when song changes
  useEffect(() => {
    if (!selectedSong) return;
    try {
      const rp = JSON.parse(localStorage.getItem('aura_recent') || '[]');
      const filtered = rp.filter(s => s.id !== selectedSong.id);
      const updated = [selectedSong, ...filtered].slice(0, 20);
      localStorage.setItem('aura_recent', JSON.stringify(updated));
      setRecentlyPlayed(updated.slice(0, 6));
    } catch (_) { }
  }, [selectedSong?.id]);

  const handleMoodSelect = async (mood) => {
    setActiveMood(mood.label);
    setLoadingMood(true);
    try {
      const results = await searchSongs(mood.query, 12);
      // Array.isArray check — agar object/null aaye toh empty array lo
      setMoodSongs(Array.isArray(results) ? results : (results?.items || results?.songs || []));
    } finally {
      setLoadingMood(false);
    }
  };

  const handlePlay = (song, list) => {
    if (playSong) playSong(song, list);
  };

  const formatViews = (v) => {
    if (!v) return '';
    if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
    if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
    return v;
  };

  return (
    <div className="home">
      {/* Hero */}
      <div className="home__hero">
        <div className="home__hero-text">
          <p className="home__hero-sub">Welcome to</p>
          <h1 className="home__hero-title">Aura Stream</h1>
          <p className="home__hero-desc">Your music, your vibe. Discover trending tracks and play by mood.</p>
        </div>
        <div className="home__hero-visual">
          <div className="hero-ring hero-ring--1" />
          <div className="hero-ring hero-ring--2" />
          <div className="hero-ring hero-ring--3" />
          <Headphones size={56} color="#ff7e3a" strokeWidth={1.5} />
        </div>
      </div>

      {/* Mood Selector */}
      <section className="home__section">
        <div className="home__section-header">
          <Music2 size={18} color="#ff7e3a" />
          <h2>Play by Mood</h2>
        </div>
        <div className="home__moods">
          {MOODS.map((mood) => (
            <button
              key={mood.label}
              className={`mood-chip${activeMood === mood.label ? ' mood-chip--active' : ''}`}
              onClick={() => handleMoodSelect(mood)}
            >
              <span>{mood.label}</span>
            </button>
          ))}
        </div>

        {/* Mood Results */}
        {activeMood && (
          <div className="home__mood-results">
            {loadingMood ? (
              <div className="home__loading-row">
                {[...Array(4)].map((_, i) => <div key={i} className="skeleton-card" />)}
              </div>
            ) : (
              <div className="home__song-row">
                {(Array.isArray(moodSongs) ? moodSongs : []).map((song) => (
                  <SongCard
                    key={song.id}
                    song={song}
                    isActive={selectedSong?.id === song.id}
                    onPlay={() => handlePlay(song, moodSongs)}
                    formatViews={formatViews}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      {/* Recently Played */}
      {recentlyPlayed.length > 0 && (
        <section className="home__section">
          <div className="home__section-header">
            <Clock size={18} color="#ff7e3a" />
            <h2>Recently Played</h2>
          </div>
          <div className="home__song-row">
            {recentlyPlayed.map((song) => (
              <SongCard
                key={song.id}
                song={song}
                isActive={selectedSong?.id === song.id}
                onPlay={() => handlePlay(song, recentlyPlayed)}
                formatViews={formatViews}
              />
            ))}
          </div>
        </section>
      )}

      {/* Trending */}
      <section className="home__section">
        <div className="home__section-header">
          <TrendingUp size={18} color="#ff7e3a" />
          <h2>Trending in India</h2>
        </div>
        {loadingTrend ? (
          <div className="home__song-row">
            {[...Array(6)].map((_, i) => <div key={i} className="skeleton-card" />)}
          </div>
        ) : (
          <div className="home__song-row">
            {trending.map((song) => (
              <SongCard
                key={song.id}
                song={song}
                isActive={selectedSong?.id === song.id}
                onPlay={() => handlePlay(song, trending)}
                formatViews={formatViews}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

const SongCard = ({ song, isActive, onPlay, formatViews }) => (
  <div className={`home-card${isActive ? ' home-card--active' : ''}`} onClick={onPlay}>
    <div className="home-card__thumb">
      <img src={song.thumbnail} alt={song.title} />
      <div className="home-card__overlay">
        <div className="home-card__play-btn">
          <Play size={20} fill="white" color="white" />
        </div>
      </div>
    </div>
    <div className="home-card__info">
      <p className="home-card__title">{song.title}</p>
      <p className="home-card__artist">{song.channelTitle}</p>
      {song.viewCount && (
        <p className="home-card__views">{formatViews(song.viewCount)} views</p>
      )}
    </div>
  </div>
);

export default Home;